<script setup lang="ts">
import { onUnmounted, shallowRef, watch } from 'vue';
import { CompanyInfo } from '@/modules/common/models';
import MarketplaceOrderDetailsDialog from '@/modules/marketplace/components/MarketplaceOrderDetailsDialog.vue';
import MarketplaceOrdersTable from '@/modules/marketplace/components/MarketplaceOrdersTable.vue';
import BrokerMarketplaceCancelAllDialog from '@/modules/broker-admin/components/BrokerMarketplaceCancelAllDialog.vue';
import CounterpartySearch from '@/modules/user-accounts/components/CounterpartySearch.vue';
import { FilterModel, IRowNode, LoadSuccessParams, SortModelItem } from 'ag-grid-enterprise';
import { marketplaceService } from '@/connect/services/marketplace';
import {
  FrontendOrder,
  QueryOrdersFilterSchema,
} from '@/connect/gen/modules/apiengine/services/oms/oms_pb';
import { useSecuritiesStore } from '@/store/securities-store';
import { useAgreementsStore } from '@/store/agreements-store';
import { useSocketStore } from '@/store/socket-store';
import { create } from '@/connect';
import i18n from '@/localisation/i18n';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';

const snackbarManager = useSnackbarManager();

const securitiesStore = useSecuritiesStore();
const agreementsStore = useAgreementsStore();

const sort = shallowRef<SortModelItem>({ colId: 'updatedAt', sort: 'desc' });
const showAllMarketplaceOrders = shallowRef(false);
const detailOrderRef = shallowRef<string | null>(null);
const selectedCompany = shallowRef<CompanyInfo | null>(null);
const selectedItems = shallowRef<FrontendOrder[]>([]);
const openDialog = shallowRef<'cancelAll' | null>(null);

const tableRefresh = shallowRef((_config: { purge: boolean }) => {
  // empty
});
const resetSelection = shallowRef(() => {
  // empty
});
const refreshCellsByCriteria = shallowRef(
  (_criteria: (node: IRowNode<FrontendOrder>) => boolean, _columns: string[]) => {
    // empty
  }
);
const totalCount = shallowRef(0);

const socketStore = useSocketStore();

// when order changed (my company orders)
socketStore.useEventHandler('refreshOmsOrders', refreshOmsTable);

// when top-of-book changed (counterparty orders), my top-of-book columns need to be updated
socketStore.useEventHandler('refreshVenueTopOfBook', refreshOmsTable);

// when price changes, refresh only the priceChange column cells for affected rows
socketStore.useEventHandler('refreshInstrumentPrice', refreshPriceColumn);

function refreshOmsTable(): void {
  // table is expected to remain more or less the same,
  // no need to purge the cache, just refresh and update the rows
  tableRefresh.value({ purge: false });
}

function refreshPriceColumn(payload: { cusip: string }): void {
  refreshCellsByCriteria.value((node) => node.data?.cusip === payload.cusip, ['priceChange']);
}

watch([selectedCompany, showAllMarketplaceOrders], () => {
  resetSelection.value();
  // purge the cache to avoid slow UI animations
  tableRefresh.value({ purge: true });
});

watch(selectedCompany, async (newCompany) => {
  await agreementsStore.loadAgreements(newCompany?.companyId);
});

function onReady(config: {
  refresh: (config: { purge: boolean }) => void;
  resetSelection: () => void;
  refreshCellsByCriteria: (
    criteria: (node: IRowNode<FrontendOrder>) => boolean,
    columns: string[]
  ) => void;
  getTotalCount: () => number;
}): void {
  tableRefresh.value = config.refresh;
  resetSelection.value = config.resetSelection;
  refreshCellsByCriteria.value = config.refreshCellsByCriteria;
}

onUnmounted(async () => {
  await securitiesStore.unsubscribeFromSocketPriceUpdates();
});

async function queryData(config: {
  page: number;
  pageSize: number;
  sort: SortModelItem;
  filter: FilterModel;
  signal: AbortSignal;
}): Promise<LoadSuccessParams | undefined> {
  const result = await marketplaceService.queryOrders({
    ...config,
    filter: create(QueryOrdersFilterSchema, {
      ...config.filter,
      companyId: !selectedCompany.value
        ? undefined
        : {
            type: 'equals',
            filter: selectedCompany.value.companyId,
          },
      showAll: showAllMarketplaceOrders.value,
    }),
  });

  if (!result.success) {
    snackbarManager.show({ message: i18n.t(result.error, result.details) as string });
    return;
  }

  securitiesStore.addSecurities(result.data.instruments);
  void securitiesStore.subscribeToSocketPriceUpdates(result.data.instruments);
  totalCount.value = Number(result.data.totalCount);

  return { rowData: result.data.orders, rowCount: Number(result.data.totalCount) };
}

const marketplaceOrdersTableImmutableProps = {
  actions: ['view'],
  extraHeaders: ['companyId'],
};
</script>

<template>
  <VCard class="flex d-flex flex-column">
    <!-- Table header (filters, tabs) -->
    <div class="pa-sm-2 gap-1 d-flex flex-wrap align-center">
      <!-- Filters -->
      <CounterpartySearch
        v-model="selectedCompany"
        class="companies"
        clearable
        label="All companies"
      />

      <template v-if="selectedItems.length">
        <span class="ml-2 mr-2">
          {{ selectedItems.length ? `${selectedItems.length}/${totalCount} selected` : '' }}
        </span>

        <AuroraBtn
          color="red"
          data-test="batch-action-force-cancel"
          small
          @click="openDialog = 'cancelAll'"
        >
          Force Cancel
        </AuroraBtn>
      </template>

      <VTooltip bottom>
        <template #activator="{ on, attrs }">
          <span v-bind="attrs" class="ml-auto" v-on="on">
            <VSwitch
              v-model="showAllMarketplaceOrders"
              class="mt-0"
              data-test="show-all-orders"
              :false-value="false"
              hide-details
              label="show archived"
            />
          </span>
        </template>
        <span class="tip-background">
          Enabling show archived will include in the results Orders that were closed before today
        </span>
      </VTooltip>
    </div>

    <!-- Dialog will not monitor changes to selectedItems list, it will only be used to initialize a local list on mount. -->
    <BrokerMarketplaceCancelAllDialog
      v-if="openDialog === 'cancelAll'"
      :items="selectedItems"
      @back="openDialog = null"
      @close-modal="openDialog = null"
      @success="
        selectedItems = [];
        resetSelection();
      "
    />

    <MarketplaceOrdersTable
      v-bind="marketplaceOrdersTableImmutableProps"
      data-test="marketplace-orders-table"
      :query-data="queryData"
      :selected-items.sync="selectedItems"
      :show-all="showAllMarketplaceOrders"
      :sort="sort"
      @ready="onReady"
      @view-order="detailOrderRef = $event"
    />

    <MarketplaceOrderDetailsDialog
      v-if="detailOrderRef"
      :as-broker="true"
      :order-ref="detailOrderRef"
      @close-modal="detailOrderRef = null"
    />
  </VCard>
</template>

<style lang="scss" scoped>
.gap-1 {
  gap: 1rem;
}

.companies,
.simple-security-search,
.side {
  max-width: 20rem;
}
</style>
